"use client"

import Box from "@mui/material/Box"
import Grid from "@mui/material/Grid"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import Skeleton from "@mui/material/Skeleton"
import { ProductCard } from "@src/components"
import { ProductCardSkeleton } from "@src/components/ProductCard/ProductCardSkeleton"
import { CancelButton } from "@src/components/Button"
import Icons from "@src/components/Icons"
import Pagination from "@src/components/Pagination"
import fetchPublicClient from "@src/api/lib/fetch-public-client"
import ProductModel from "@src/models/product"
import { buildUpcomingSaleRequest } from "@src/utils/upcoming-sale-request.mjs"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Navigation } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"

interface Props {
  pageSize: number
  category?: string
  sortBy?: string
  direction?: string
  page?: number
  showPagination?: boolean
  onPageChange?: (page: number) => void
  onHasProductsChange?: (hasProducts: boolean) => void
  onViewMore?: () => void
  carousel?: boolean
  compactMobile?: boolean
  mobileCardDetails?: boolean
}

const UpcomingSaleProductsSection = ({
  pageSize,
  category,
  sortBy,
  direction,
  page = 1,
  showPagination = false,
  onPageChange,
  onHasProductsChange,
  onViewMore,
  carousel = false,
  compactMobile = false,
  mobileCardDetails = false
}: Props) => {
  const { t } = useTranslation()
  const [products, setProducts] = useState<ProductModel[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let current = true
    setLoading(true)
    setProducts([])
    setTotal(0)
    onHasProductsChange?.(false)

    fetchPublicClient(
      buildUpcomingSaleRequest({ pageSize, page, category, sortBy, direction })
    )
      .then(response => {
        if (!current) return
        const data = response?.data || {}
        setProducts(data.items || [])
        setTotal(Number(data.total || 0))
        onHasProductsChange?.(Number(data.total || 0) > 0)
      })
      .catch(error => {
        if (!current) return
        console.error("Failed to load upcoming sale products:", error)
        setProducts([])
        setTotal(0)
        onHasProductsChange?.(false)
      })
      .finally(() => {
        if (current) setLoading(false)
      })

    return () => {
      current = false
    }
  }, [pageSize, page, category, sortBy, direction, onHasProductsChange])

  if (!loading && !products.length && total === 0) return null

  return (
    <Box
      component="section"
      aria-labelledby="upcoming-sale-heading"
      aria-busy={loading}
      sx={{
        position: "relative",
        overflow: "hidden",
        isolation: "isolate",
        mb: { xs: 4, md: 7 },
        p: { xs: compactMobile ? 0 : 2, sm: 3, md: 4 },
        borderRadius: 0,
        border: { xs: compactMobile ? 0 : "1px solid #f0d5d0", sm: "1px solid #f0d5d0" },
        background: { xs: compactMobile ? "transparent" : "radial-gradient(ellipse at 92% 0%, rgba(244, 143, 122, .2), transparent 34%), linear-gradient(135deg, #fff8f5 0%, #fffdfb 54%, #fff4ef 100%)", sm: "radial-gradient(ellipse at 92% 0%, rgba(244, 143, 122, .2), transparent 34%), linear-gradient(135deg, #fff8f5 0%, #fffdfb 54%, #fff4ef 100%)" },
        "&::before": {
          content: '""',
          display: { xs: compactMobile ? "none" : "block", sm: "block" },
          position: "absolute",
          zIndex: -1,
          width: 240,
          height: 240,
          right: { xs: -140, md: -90 },
          top: { xs: -150, md: -130 },
          border: "1px solid rgba(180, 65, 48, .13)",
          borderRadius: "50%",
          boxShadow: "0 0 0 24px rgba(180,65,48,.025), 0 0 0 48px rgba(180,65,48,.02)"
        }
      }}
    >
      <Stack
        direction={{ xs: compactMobile ? "row" : "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: compactMobile ? "center" : "flex-start", sm: "flex-end" }}
        gap={1.5}
        sx={{ mb: { xs: compactMobile ? 2 : 2.5, md: 3.5 } }}
      >
        <Box>
          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.25}>
            <Typography
              id="upcoming-sale-heading"
              component="h2"
              sx={{ color: "#231c1a", fontSize: { xs: compactMobile ? 20 : 25, sm: 29, md: 36 }, fontWeight: 750, letterSpacing: compactMobile ? "-.035em" : "-.045em", lineHeight: compactMobile ? 1.2 : 1.08 }}
            >
              {t("upcoming_flash_sale_products")}
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{ display: { xs: compactMobile ? "none" : "flex", sm: "flex" }, px: 1.25, py: 0.6, borderRadius: 99, bgcolor: "rgba(212,72,52,.08)" }}
            >
              <Box
                component="span"
                sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#d44834", boxShadow: "0 0 0 3px rgba(212,72,52,.12)" }}
              />
              <Typography
                component="span"
                sx={{ color: "#a43a2c", fontSize: 9, fontWeight: 800, letterSpacing: ".12em", textTransform: "uppercase", whiteSpace: "nowrap" }}
              >
                Coming soon · Flash Sale
              </Typography>
            </Stack>
          </Stack>
          <Typography sx={{ display: { xs: compactMobile ? "none" : "block", sm: "block" }, mt: 1, color: "#786a65", fontSize: { xs: 13, md: 14 } }}>
            {t("upcoming_flash_sale_desc", { defaultValue: "Những thiết kế sắp lên kệ với mức giá ưu đãi đặc biệt." })}
          </Typography>
        </Box>
        <Typography
          component="span"
          sx={{ px: { xs: compactMobile ? 0 : 1.5, sm: 1.5 }, py: .75, border: { xs: compactMobile ? 0 : "1px solid #e8c2ba", sm: "1px solid #e8c2ba" }, borderRadius: 99, color: "#a43a2c", fontSize: { xs: compactMobile ? 9 : 11, sm: 11 }, fontWeight: 750, letterSpacing: { xs: compactMobile ? 0 : ".08em", sm: ".08em" }, whiteSpace: "nowrap", flexShrink: 0 }}
        >
          {loading ? <Skeleton variant="text" width={72} /> : `${String(total).padStart(2, "0")} ${t("products_count_label", { defaultValue: "SẢN PHẨM" })}`}
        </Typography>
      </Stack>
      {carousel ? (
        <Box
          sx={{
            width: "100%",
            position: "relative",
            "& .swiper-button-prev, & .swiper-button-next": {
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: "#292522",
              color: "#fff",
              boxShadow: "0 3px 12px rgba(0,0,0,.16)",
              "&::after": { fontSize: 13, fontWeight: 800 },
              "&.swiper-button-disabled": { opacity: 0, pointerEvents: "none" }
            },
            "@media (max-width:599px)": { "& .swiper-button-prev, & .swiper-button-next": { display: "none" } }
          }}
        >
          <Swiper
            key={loading ? "upcoming-sale-loading" : "upcoming-sale-loaded"}
            modules={[Navigation]}
            navigation={!loading}
            slidesPerView={1.25}
            spaceBetween={12}
            observer
            observeParents
            updateOnWindowResize
            breakpoints={{ 600: { slidesPerView: 2, spaceBetween: 16 }, 900: { slidesPerView: 3, spaceBetween: 20 }, 1200: { slidesPerView: 4, spaceBetween: 22 } }}
            style={{ padding: "2px 2px 12px", height: "auto" }}
            className="upcoming-sale-carousel"
          >
            {loading ? Array.from({ length: 4 }, (_, index) => (
              <SwiperSlide key={`loading-${index}`} style={{ height: "auto" }}>
                <Box sx={{ width: "100%", height: "100%", minWidth: 0 }}>
                  <ProductCardSkeleton />
                </Box>
              </SwiperSlide>
            )) : products.map((product, index) => (
              <SwiperSlide key={product.id} style={{ height: "auto", alignSelf: "stretch" }}>
                <Box sx={{ animation: "upcomingSaleReveal .45s both", animationDelay: `${index * 60}ms`, "@keyframes upcomingSaleReveal": { from: { opacity: 0, transform: "translateY(10px)" }, to: { opacity: 1, transform: "translateY(0)" } }, "@media (prefers-reduced-motion: reduce)": { animation: "none" } }}>
                  <ProductCard product={product} mobileSalePanelBelowImage={mobileCardDetails} />
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      ) : (
        <Grid
          container
          spacing={{ xs: 1.25, sm: 2, md: 2.5 }}
          sx={{
            marginLeft: { xs: "-10px", sm: "-16px", md: "-20px" },
            width: { xs: "calc(100% + 10px)", sm: "calc(100% + 16px)", md: "calc(100% + 20px)" }
          }}
        >
          {loading ? Array.from({ length: Math.min(pageSize, 4) }, (_, index) => (
            <Grid key={`loading-${index}`} item xs={6} lg={3}>
              <ProductCardSkeleton />
            </Grid>
          )) : products.map((product, index) => (
            <Grid key={product.id} item xs={6} lg={3} sx={{ animation: "upcomingSaleReveal .45s both", animationDelay: `${index * 70}ms`, "@keyframes upcomingSaleReveal": { from: { opacity: 0, transform: "translateY(10px)" }, to: { opacity: 1, transform: "translateY(0)" } }, "@media (prefers-reduced-motion: reduce)": { animation: "none" } }}>
              <Box sx={{ height: "100%", "& > *": { height: "100%" } }}>
                <ProductCard product={product} mobileSalePanelBelowImage={mobileCardDetails} />
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
      {!loading && showPagination && onPageChange && (
        <Pagination
          page={page}
          total={Math.ceil(total / pageSize)}
          onChange={onPageChange}
        />
      )}
      {!loading && !showPagination && total > pageSize && onViewMore && (
        <CancelButton
          type="button"
          height="40px"
          label={t("view_all")}
          endIcon={<Icons name="arrow-right" />}
          minHeight="40px"
          sx={{
            margin: { xs: "24px auto 0", md: "48px auto 0" },
            minWidth: { xs: "220px !important", md: "328px !important" }
          }}
          onClick={onViewMore}
        />
      )}
    </Box>
  )
}

export default UpcomingSaleProductsSection
